import { API_BASE_URL } from './api';

export interface ConversationMessage {
  id?: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  message_order: number;
}

export interface ConversationSummary {
  id: string;
  user_id: string;
  title: string;
  chatbot_id: string;
  chatbot_name: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message_preview?: string;
}

export interface ConversationDetail {
  id: string;
  user_id: string;
  title: string;
  chatbot_id: string;
  chatbot_name: string;
  created_at: string;
  updated_at: string;
  messages: ConversationMessage[];
}

export interface CreateConversationRequest {
  title: string;
  chatbot_id: string;
  chatbot_name: string;
}

export class ConversationService {
  private static getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  static async createConversation(data: CreateConversationRequest): Promise<ConversationSummary> {
    const response = await fetch(`${API_BASE_URL}/api/conversations`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create conversation');
    }

    return response.json();
  }

  static async getConversations(): Promise<ConversationSummary[]> {
    console.log('🔄 ConversationService.getConversations() called');
    console.log('📡 API URL:', `${API_BASE_URL}/api/conversations`);
    console.log('🔑 Auth headers:', this.getAuthHeaders());
    
    const response = await fetch(`${API_BASE_URL}/api/conversations`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    console.log('📥 Response status:', response.status);
    console.log('📥 Response ok:', response.ok);

    if (!response.ok) {
      const error = await response.json();
      console.error('❌ API Error:', error);
      throw new Error(error.detail || 'Failed to fetch conversations');
    }

    const data = await response.json();
    console.log('✅ Conversations data:', data);
    return data;
  }

  static async getConversation(conversationId: string): Promise<ConversationDetail> {
    console.log('🔄 ConversationService.getConversation() called with ID:', conversationId);
    console.log('📡 API URL:', `${API_BASE_URL}/api/conversations/${conversationId}`);
    
    const response = await fetch(`${API_BASE_URL}/api/conversations/${conversationId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    console.log('📥 Response status:', response.status);
    console.log('📥 Response ok:', response.ok);

    if (!response.ok) {
      const error = await response.json();
      console.error('❌ API Error:', error);
      throw new Error(error.detail || 'Failed to fetch conversation');
    }

    const data = await response.json();
    console.log('✅ Conversation detail data:', data);
    return data;
  }

  static async addMessage(conversationId: string, message: {
    role: 'user' | 'assistant';
    content: string;
    message_order: number;
  }): Promise<ConversationMessage> {
    const response = await fetch(`${API_BASE_URL}/api/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        conversation_id: conversationId,
        ...message,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to add message');
    }

    return response.json();
  }

  static async deleteConversation(conversationId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/conversations/${conversationId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete conversation');
    }
  }
}